import { useEffect, useRef } from 'react';
import type { LabelPreview, LabelTemplate } from '../api.ts';

/**
 * Этикетка для карманного термопринтера (Niimbot D11/D110, Phomemo D30
 * и их близнецы).
 *
 * Рисуется на клиенте, а не на сервере, и вот почему.
 *
 *   1. Эти принтеры печатают не документы, а КАРТИНКУ, которую им
 *      отдаёт фирменное приложение. Значит нужен PNG, а не PDF.
 *   2. Картинка должна быть ровно в разрешении головки — 203 dpi, восемь
 *      точек на миллиметр. Если её отмасштабирует приложение принтера,
 *      модуль QR размажется по границам точек, и код перестанет читаться.
 *      Поэтому модуль кода принудительно выравнивается на целое число
 *      точек, а вся картинка рисуется в натуральных пикселях головки.
 *   3. Кириллица. На сервере в контейнере шрифтов нет вовсе, а в браузере
 *      они есть всегда — и на телефоне, где и живёт приложение принтера.
 *
 * Термопечать двухцветная: серого не бывает, есть точка или нет. Поэтому
 * готовый холст прогоняется через порог — полутона от сглаживания шрифта
 * превратились бы в грязный растр.
 */

/** Точек на миллиметр при 203 dpi — родное разрешение таких головок. */
export function dotsPerMm(dpi: number): number {
  return dpi / 25.4;
}

export type RollRender = {
  canvas: HTMLCanvasElement;
  widthPx: number;
  heightPx: number;
  qrSideMm: number;
  moduleMm: number;
};

export function renderRollLabel(
  label: LabelPreview,
  template: LabelTemplate,
): RollRender | null {
  if (!label.qr) return null;

  const dpi = template.dpi ?? 203;
  const dots = dotsPerMm(dpi);

  const widthPx = Math.round(template.label.width * dots);
  const heightPx = Math.round(template.label.height * dots);
  const bandPx = Math.round((template.printWidth ?? template.label.height) * dots);
  const bandTop = Math.round((heightPx - bandPx) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, widthPx, heightPx);

  // ── Код ────────────────────────────────────────────────────────────
  // Модуль — целое число точек. Три точки на модуль при 203 dpi дают
  // 0.37 мм: мельче печатать бессмысленно, крупнее не влезает в ленту.
  const size = label.qr.size;
  const padPx = Math.round(0.8 * dots);
  // Не шире печатной полосы и не длиннее сорока пяти процентов ленты:
  // на широкой ленте код иначе занимает её целиком, и номер печатать
  // некуда.
  const qrLimit = Math.min(bandPx - 2, widthPx * 0.45);
  const module = Math.max(2, Math.floor(qrLimit / size));
  const qrPx = module * size;
  const qrX = padPx;
  const qrY = bandTop + Math.round((bandPx - qrPx) / 2);

  ctx.fillStyle = '#000';
  label.qr.rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x += 1) {
      if (row[x] === '1') ctx.fillRect(qrX + x * module, qrY + y * module, module, module);
    }
  });

  // ── Текст ──────────────────────────────────────────────────────────
  // Порядок другой, чем на листовой этикетке: сначала инвентарный номер.
  // Он переживёт и домен, и саму наклейку — это единственное, что
  // связывает бумажку с камнем, если QR перестанет читаться (ТЗ 6.5).
  const quiet = module * 4;
  const textX = qrX + qrPx + quiet;
  const textWidth = widthPx - textX - Math.round(0.5 * dots);

  const number = label.footer.split(' · ')[0] ?? label.footer;

  // Строки те же три, что и на листовой этикетке (ТЗ 6.2), но в другом
  // порядке: сначала номер. Месторождение — полноценная строка, а не
  // излишество: «Вивианит» без «Розе, Мадагаскар» на коробке отвечает
  // только на половину вопроса.
  //
  // Место для третьей строки есть: при полосе 12 мм три строки занимают
  // около 9 мм. Не хватает не высоты, а ДЛИНЫ — на ленте 12 × 22 текстовая
  // колонка всего 11 мм, и месторождение там превратилось бы в огрызок.
  // Поэтому третья строка появляется, когда колонке есть куда её положить.
  const nameSize = Math.round(bandPx * 0.26);
  const textMm = textWidth / dots;
  const withPlace = Boolean(label.place) && textMm >= 14;

  const lines: { text: string; weight: number; size: number }[] = withPlace
    ? [
        { text: number, weight: 600, size: Math.round(bandPx * 0.28) },
        { text: label.title, weight: 400, size: Math.round(bandPx * 0.24) },
        { text: label.place, weight: 400, size: Math.round(bandPx * 0.21) },
      ]
    : [
        { text: number, weight: 600, size: Math.round(bandPx * 0.3) },
        { text: label.title, weight: 400, size: nameSize },
      ];

  ctx.font = `400 ${lines[1]!.size}px "IBM Plex Sans", system-ui, sans-serif`;
  const fullTitleWidth = ctx.measureText(label.title).width;

  const family = '"IBM Plex Sans", system-ui, sans-serif';
  ctx.fillStyle = '#000';
  ctx.textBaseline = 'top';

  if (fullTitleWidth > textWidth) {
    lines[1]!.text = label.title.split(' (')[0] ?? label.title;
  }

  // Подгоняем кегль каждой строки под ширину: обрезать строку хуже, чем
  // набрать её мельче. Ниже шести пунктов не опускаемся (ТЗ 6.2) — на
  // головке 203 dpi это чуть больше двух миллиметров, предел читаемости.
  const floorPx = Math.round(6 * (dots / 2.835));
  for (const line of lines) {
    while (line.size > floorPx) {
      ctx.font = `${line.weight} ${line.size}px ${family}`;
      if (ctx.measureText(line.text).width <= textWidth) break;
      line.size -= 1;
    }
  }

  const gap = Math.max(1, Math.round(dots * 0.3));
  const block = lines.reduce((sum, line) => sum + line.size, 0) + gap * (lines.length - 1);
  let cursor = bandTop + Math.round((bandPx - block) / 2);

  for (const line of lines) {
    ctx.font = `${line.weight} ${line.size}px ${family}`;
    ctx.fillText(cut(ctx, line.text, textWidth), textX, cursor);
    cursor += line.size + gap;
  }

  threshold(ctx, widthPx, heightPx);

  return {
    canvas,
    widthPx,
    heightPx,
    qrSideMm: qrPx / dots,
    moduleMm: module / dots,
  };
}

/** Обрезка по ширине — когда уменьшать кегль уже некуда. */
function cut(ctx: CanvasRenderingContext2D, text: string, width: number): string {
  if (ctx.measureText(text).width <= width) return text;
  let result = text;
  while (result.length > 1 && ctx.measureText(`${result}…`).width > width) {
    result = result.slice(0, -1);
  }
  return `${result.trimEnd()}…`;
}

/**
 * В чёрно-белое. Термоголовка ставит точку или не ставит; полутона,
 * которые оставило сглаживание шрифта, она превратит в грязь.
 */
function threshold(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const luminance = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
    const value = luminance < 160 ? 0 : 255;
    data[i] = value;
    data[i + 1] = value;
    data[i + 2] = value;
    data[i + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
}

/** Предпросмотр: тот же холст, показанный в натуральную величину. */
export function RollLabelPreview({
  label,
  template,
  onRendered,
}: {
  label: LabelPreview;
  template: LabelTemplate;
  onRendered?: (render: RollRender) => void;
}) {
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = host.current;
    if (!node) return;
    const render = renderRollLabel(label, template);
    node.replaceChildren();
    if (!render) return;

    // Показываем в миллиметрах: этикетка — будущий физический объект.
    render.canvas.style.width = `${template.label.width}mm`;
    render.canvas.style.height = `${template.label.height}mm`;
    render.canvas.className = 'roll-canvas';
    node.append(render.canvas);
    onRendered?.(render);
    // onRendered намеренно не в зависимостях: он пересоздаётся на каждый
    // рендер родителя, и от него холст перерисовывался бы без нужды.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, template]);

  return <div ref={host} className="roll-preview" />;
}

/** Сохранить этикетку картинкой — её и подсовывают приложению принтера. */
export async function downloadRollLabel(
  label: LabelPreview,
  template: LabelTemplate,
): Promise<void> {
  const render = renderRollLabel(label, template);
  if (!render) return;

  const blob = await new Promise<Blob | null>((resolve) =>
    render.canvas.toBlob(resolve, 'image/png'),
  );
  if (!blob) return;

  const number = (label.footer.split(' · ')[0] ?? 'label').replace(/[^\w-]/g, '');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${number}-${template.label.height}x${template.label.width}.png`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
